// Cola de envío del vendedor (2026-10-25): lo que se toma sin señal queda aquí y sale solo cuando
// vuelve la conexión, cada uno con su clave fija (el ERP no lo registra dos veces aunque se reenvíe).
// Sin red, servidor caído o sesión vencida: se reintenta. Un rechazo del ERP (4xx): queda con su motivo
// para que el vendedor lo vea y decida (reintentar o descartar).
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { api, ErrorApi } from '../api/cliente';
import {
  CLAVES,
  escribir,
  leer,
  type ItemCola,
  type ItemEnviado,
  type ResultadoPedido,
} from './almacen';

const CADA = 20_000;
const MAX_ENVIADOS = 80;

interface Cola {
  pendientes: ItemCola[];
  enviados: ItemEnviado[];
  enviando: boolean;
  /** El último intento falló por sesión vencida: hay que volver a ingresar. */
  sinSesion: boolean;
  enLinea: boolean;
  agregar(item: Omit<ItemCola, 'estado' | 'intentos' | 'error'>): Promise<void>;
  enviarAhora(): void;
  descartar(id: string): Promise<void>;
  reintentar(id: string): Promise<void>;
}

const Ctx = createContext<Cola | null>(null);

const reintentable = (e: unknown) =>
  !(e instanceof ErrorApi) ||
  e.status === 0 ||
  e.status === 401 ||
  e.status === 408 ||
  e.status === 429 ||
  e.status >= 500;

export function ProveedorCola({
  children,
  alEnviar,
}: {
  children: ReactNode;
  /** Algo llegó al ERP: refrescar «Mis pedidos». */
  alEnviar?: () => void;
}) {
  const [pendientes, setPendientes] = useState<ItemCola[]>([]);
  const [enviados, setEnviados] = useState<ItemEnviado[]>([]);
  const [enviando, setEnviando] = useState(false);
  const [sinSesion, setSinSesion] = useState(false);
  const [enLinea, setEnLinea] = useState(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );
  const cola = useRef<ItemCola[]>([]);
  const ocupado = useRef(false);
  const avisar = useRef(alEnviar);
  avisar.current = alEnviar;

  const guardar = useCallback(async (nueva: ItemCola[]) => {
    cola.current = nueva;
    setPendientes(nueva);
    await escribir(CLAVES.cola, nueva);
  }, []);

  useEffect(() => {
    void (async () => {
      const c = (await leer<ItemCola[]>(CLAVES.cola)) ?? [];
      cola.current = c;
      setPendientes(c);
      setEnviados((await leer<ItemEnviado[]>(CLAVES.enviados)) ?? []);
    })();
  }, []);

  const procesar = useCallback(async () => {
    if (ocupado.current) return;
    const turno = cola.current.filter((x) => x.estado === 'PENDIENTE');
    if (!turno.length) return;
    ocupado.current = true;
    setEnviando(true);
    try {
      for (const item of turno) {
        try {
          const ruta = item.tipo === 'PEDIDO' ? '/vendedor/pedidos' : '/vendedor/visitas';
          const r = await api.post<ResultadoPedido | { ok: true }>(ruta, item.cuerpo, {
            idempotencia: item.clave,
            silenciar401: true,
          });
          setSinSesion(false);
          setEnLinea(true);
          const enviado: ItemEnviado = {
            id: item.id,
            tipo: item.tipo,
            empresaId: item.empresaId,
            clave: item.clave,
            cuerpo: item.cuerpo,
            cliente: item.cliente,
            totalAprox: item.totalAprox,
            lineas: item.lineas,
            creadoEn: item.creadoEn,
            enviadoEn: new Date().toISOString(),
            resultado: item.tipo === 'PEDIDO' ? (r as ResultadoPedido) : null,
          };
          const previos = (await leer<ItemEnviado[]>(CLAVES.enviados)) ?? [];
          const lista = [enviado, ...previos.filter((x) => x.id !== item.id)].slice(
            0,
            MAX_ENVIADOS,
          );
          await escribir(CLAVES.enviados, lista);
          setEnviados(lista);
          await guardar(cola.current.filter((x) => x.id !== item.id));
          avisar.current?.();
        } catch (e) {
          if (e instanceof ErrorApi && e.status === 401) setSinSesion(true);
          if (e instanceof ErrorApi && e.status === 0) setEnLinea(false);
          const sigue = reintentable(e);
          await guardar(
            cola.current.map((x) =>
              x.id === item.id
                ? {
                    ...x,
                    intentos: x.intentos + 1,
                    estado: sigue ? 'PENDIENTE' : 'ERROR',
                    error: e instanceof Error ? e.message : String(e),
                  }
                : x,
            ),
          );
          // Sin red o sin sesión, los demás tampoco van a pasar: se espera al próximo turno.
          if (sigue) break;
        }
      }
    } finally {
      ocupado.current = false;
      setEnviando(false);
    }
  }, [guardar]);

  useEffect(() => {
    const t = setInterval(() => void procesar(), CADA);
    const online = () => {
      setEnLinea(true);
      void procesar();
    };
    const offline = () => setEnLinea(false);
    window.addEventListener('online', online);
    window.addEventListener('offline', offline);
    const primero = setTimeout(() => void procesar(), 500);
    return () => {
      clearInterval(t);
      clearTimeout(primero);
      window.removeEventListener('online', online);
      window.removeEventListener('offline', offline);
    };
  }, [procesar]);

  const agregar = useCallback<Cola['agregar']>(
    async (item) => {
      await guardar([...cola.current, { ...item, estado: 'PENDIENTE', intentos: 0, error: null }]);
      void procesar();
    },
    [guardar, procesar],
  );
  const descartar = useCallback(
    (id: string) => guardar(cola.current.filter((x) => x.id !== id)),
    [guardar],
  );
  const reintentar = useCallback(
    async (id: string) => {
      await guardar(
        cola.current.map((x) => (x.id === id ? { ...x, estado: 'PENDIENTE', error: null } : x)),
      );
      void procesar();
    },
    [guardar, procesar],
  );

  const valor = useMemo<Cola>(
    () => ({
      pendientes,
      enviados,
      enviando,
      sinSesion,
      enLinea,
      agregar,
      enviarAhora: () => void procesar(),
      descartar,
      reintentar,
    }),
    [pendientes, enviados, enviando, sinSesion, enLinea, agregar, procesar, descartar, reintentar],
  );
  return <Ctx.Provider value={valor}>{children}</Ctx.Provider>;
}

export function useCola(): Cola {
  const c = useContext(Ctx);
  if (!c) throw new Error('useCola fuera de ProveedorCola');
  return c;
}
